import generateUuid from "src/app/libs/generateUuid";

import { API_URLS } from "src/utils/API_URLS";
import * as actionTypes from "./mainTypes";
import {
  CreateNewProjectData,
  HowItWorksModalDataProps,
  NewProjectBriefData,
  NotificationType,
  SettingsModalDataProps,
  SubscriptionEntitlementError,
  WsProjectReportShowcasePdfReadyModalData,
} from "./mainReducer";

export function getTranslations(locale: string | null) {
  const lang = locale || "en_GB";
  const url = API_URLS.getTranslationsForLocale.replace(":lang:", lang);
  return {
    type: actionTypes.GET_TRANSLATIONS,
    payload: {
      request: {
        url,
      },
    },
  };
}

export function getTranslationsForMember() {
  return {
    type: actionTypes.GET_TRANSLATIONS,
    payload: {
      request: {
        url: API_URLS.getTranslationsForMember,
      },
    },
  };
}

export function setLanguage(lang = "en_GB") {
  return {
    type: actionTypes.SET_LANGUAGE,
    payload: {
      request: {
        method: "post",
        url: API_URLS.setLanguage,
        data: {
          locale: lang,
        },
      },
    },
  };
}

export function identify(workspaceUuid?: string) {
  return {
    type: actionTypes.GET_IDENTIFY,
    payload: {
      request: {
        url: workspaceUuid
          ? `${API_URLS.getIdentity}?workspaceUuid=${workspaceUuid}`
          : API_URLS.getIdentity,
      },
    },
  };
}

export function isLoggedIn() {
  return {
    type: actionTypes.GET_IS_LOGGED_IN,
    payload: {
      request: {
        url: API_URLS.getIdentity,
      },
    },
  };
}

export function setWebSocket(
  workspaceUuid: string,
  userId: string | undefined,
  challenge: number | undefined,
) {
  return {
    type: actionTypes.SET_WEBSOCKET,
    payload: {
      workspaceUuid,
      userId,
      challenge,
    },
  };
}

export function clearWebSocket() {
  return {
    type: actionTypes.CLEAR_WEBSOCKET,
  };
}

export function setActiveWorkspaceUuid(workspaceUuid: string) {
  return {
    type: actionTypes.SET_ACTIVE_WORKSPACE_UUID,
    payload: {
      workspaceUuid,
    },
  };
}

export function setChatwootUnreadMessages(counter: number) {
  return {
    type: actionTypes.SET_CHATWOOT_UNREAD_MESSAGES,
    payload: {
      counter,
    },
  };
}

export function getWorkspacesList() {
  return {
    type: actionTypes.GET_WORKSPACES_LIST,
    payload: {
      request: {
        url: API_URLS.listWorkspacesForUser,
      },
    },
  };
}

export function callReducerFunction(type: string, payload: any) {
  return {
    type,
    payload,
  };
}

export function getCreatorDiscoveryToolUrl(wsWorkspaceUuid: string) {
  return {
    type: actionTypes.GET_CREATOR_DISCOVERY_TOOL_URL,
    payload: {
      request: {
        url: API_URLS.getCreatorDiscoveryToolUrl.replace(
          ":wsWorkspaceUuid:",
          wsWorkspaceUuid,
        ),
      },
    },
  };
}

interface SendNotificationProps {
  type: NotificationType;
  message?: string;
  id?: string;
}

export function sendNotification({ type, message, id }: SendNotificationProps) {
  return {
    type: actionTypes.SEND_NOTIFICATION,
    payload: {
      id: id ?? generateUuid(),
      type,
      message,
    },
  };
}

export function dropNotification(id: string) {
  return {
    type: actionTypes.DROP_NOTIFICATION,
    payload: {
      id,
    },
  };
}

export function setPdfReadyModalData(
  data: WsProjectReportShowcasePdfReadyModalData | null,
) {
  return {
    type: actionTypes.SET_PDF_READY_MODAL_DATA,
    payload: {
      data,
    },
  };
}

export function setSettingsModalData(
  settingsModalData: SettingsModalDataProps,
) {
  return {
    type: actionTypes.SET_SETTINGS_MODAL_DATA,
    payload: { settingsModalData },
  };
}

export function setHowItWorksModalData(
  howItWorksModalData: HowItWorksModalDataProps,
) {
  return {
    type: actionTypes.SET_HOW_IT_WORKS_MODAL_DATA,
    payload: { howItWorksModalData },
  };
}

export function setCreateNewProjectData(
  createNewProjectData: CreateNewProjectData,
) {
  return {
    type: actionTypes.SET_CREATE_NEW_PROJECT_DATA,
    payload: { createNewProjectData },
  };
}

export function clearCreateNewProjectData() {
  return {
    type: actionTypes.CLEAR_CREATE_NEW_PROJECT_DATA,
  };
}

export function setSubscriptionEntitlementErrors(
  subscriptionEntitlementErrors: SubscriptionEntitlementError[],
) {
  return {
    type: actionTypes.SET_SUBSCRIPTION_ENTITLEMENT_ERRORS,
    payload: { subscriptionEntitlementErrors },
  };
}

export function setWorkspaceListDropdownVisible(visible: boolean) {
  return {
    type: actionTypes.SET_WORKSPACE_LIST_DROPDOWN_VISIBLE,
    payload: { visible },
  };
}

export function getWelcomePageData(workspaceUuid: string) {
  const url = API_URLS.getWelcomePageData.replace(
    ":workspaceUuid:",
    workspaceUuid,
  );

  return {
    type: actionTypes.GET_WELCOME_PAGE_DATA,
    payload: {
      request: { url },
    },
  };
}

export function getTeamsForWorkspace(workspaceUuid: string) {
  return {
    type: actionTypes.GET_TEAMS_FOR_WORKSPACE,
    payload: {
      request: {
        url: API_URLS.teamsForWorkspace.replace(
          ":wsWorkspaceUuid:",
          workspaceUuid,
        ),
      },
    },
  };
}

export function setOpenAddBriefPaneForNewProject(isOpen: boolean) {
  return {
    type: actionTypes.SET_OPEN_ADD_BRIEF_PANE_FOR_NEW_PROJECT,
    payload: {
      isOpen,
    },
  };
}

export function setLoadingBriefForNewProject(isLoading: boolean) {
  return {
    type: actionTypes.SET_LOADING_BRIEF_FOR_NEW_PROJECT,
    payload: {
      isLoading,
    },
  };
}

export function setBriefDataForNewProject(data: NewProjectBriefData | null) {
  return {
    type: actionTypes.SET_BRIEF_DATA_FOR_NEW_PROJECT,
    payload: {
      data,
    },
  };
}

export function setShowTableSearch(showTableSearch: boolean) {
  return {
    type: actionTypes.UPDATE_SHOW_TABLE_SEARCH,
    payload: {
      showTableSearch,
    },
  };
}
